import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, ArrowForward, CloudUpload, Close, ContentCopy, Delete, Save, Search } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import AppService from '../services/AppService';
import AppViolationService from '../services/AppViolationService';
import type { App } from '../types/app';
import type { AppViolationReviewReport, ViolationIssue } from '../types/appViolation';

const REVIEW_MODELS = [
  { value: '', label: 'Default (Gemini 2.5 Flash Lite)' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gpt-4.1-mini', label: 'OpenAI GPT-4.1 Mini' },
];

export default function AppStoreViolationChecker() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [model, setModel] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [screenshotPreviewUrls, setScreenshotPreviewUrls] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewSource, setPreviewSource] = useState<'upload' | 'report'>('upload');
  const [savedReports, setSavedReports] = useState<AppViolationReviewReport[]>([]);
  const [currentReport, setCurrentReport] = useState<AppViolationReviewReport | null>(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedApp = useMemo(
    () => apps.find((app) => app.appId === selectedAppId) || null,
    [apps, selectedAppId]
  );

  const uploadPreviewItems = useMemo(
    () =>
      screenshots
        .map((file, index) => ({
          fileName: file.name,
          index,
          url: screenshotPreviewUrls[index],
        }))
        .filter((item) => Boolean(item.url)) as Array<{ fileName: string; index: number; url: string }>,
    [screenshotPreviewUrls, screenshots]
  );

  const reportPreviewItems = useMemo(
    () =>
      (currentReport?.screenshots || [])
        .map((item, index) => ({
          fileName: item.fileName,
          index,
          url: item.downloadUrl || '',
        }))
        .filter((item) => Boolean(item.url)) as Array<{ fileName: string; index: number; url: string }>,
    [currentReport?.screenshots]
  );

  const activePreviewItems = previewSource === 'report' ? reportPreviewItems : uploadPreviewItems;

  const screenshotPreviewLookup = useMemo(() => {
    const lookup = new Map<string, { url: string; index: number; fileName: string }>();
    (currentReport?.screenshots || []).forEach((screenshot, index) => {
      if (!screenshot.downloadUrl) return;
      lookup.set(screenshot.fileName.trim().toLowerCase(), {
        url: screenshot.downloadUrl,
        index,
        fileName: screenshot.fileName,
      });
    });
    return lookup;
  }, [currentReport?.screenshots]);

  useEffect(() => {
    const urls = screenshots.map((file) => URL.createObjectURL(file));
    setScreenshotPreviewUrls(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [screenshots]);

  useEffect(() => {
    if (!previewOpen) return;
    if (activePreviewItems.length === 0) {
      setPreviewOpen(false);
      setPreviewIndex(0);
      return;
    }
    if (previewIndex > activePreviewItems.length - 1) {
      setPreviewIndex(0);
    }
  }, [activePreviewItems.length, previewIndex, previewOpen]);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const userApps = await AppService.getUserApps(user.email);
    setApps(userApps);
  }, [user?.email]);

  const loadReports = useCallback(async () => {
    if (!user?.email || !selectedAppId) {
      setSavedReports([]);
      return;
    }
    const reports = await AppViolationService.getReportsByApp(user.email, selectedAppId);
    setSavedReports(reports);
  }, [selectedAppId, user?.email]);

  useEffect(() => {
    loadApps().catch((loadError) => {
      console.error(loadError);
      setError('Failed to load registered apps.');
    });
  }, [loadApps]);

  useEffect(() => {
    loadReports().catch((loadError) => {
      console.error(loadError);
      setError('Failed to load saved guideline reviews.');
    });
  }, [loadReports]);

  const handleUploadScreenshots = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      file.type.startsWith('image/')
    );
    setScreenshots(files);
    setPreviewIndex(0);
  };

  const handleRemoveScreenshot = (indexToRemove: number) => {
    setScreenshots((prev) => {
      const next = prev.filter((_, index) => index !== indexToRemove);
      if (next.length === 0) {
        setPreviewOpen(false);
        setPreviewIndex(0);
      } else if (previewIndex >= next.length) {
        setPreviewIndex(next.length - 1);
      } else if (indexToRemove < previewIndex) {
        setPreviewIndex((prevIndex) => Math.max(0, prevIndex - 1));
      }
      return next;
    });
  };

  const handleRunReview = async () => {
    if (!user?.email) return;
    if (!selectedApp) {
      setError('Select a registered app first.');
      return;
    }
    if (screenshots.length === 0) {
      setError('Upload at least one screenshot to review.');
      return;
    }

    setLoadingReview(true);
    setError(null);
    setSuccess(null);
    try {
      const report = await AppViolationService.reviewScreenshots({
        userId: user.email,
        app: selectedApp,
        screenshots,
        model: model || undefined,
      });
      setCurrentReport(report);
      setTab(1);
      setSuccess('Guideline review completed.');
    } catch (reviewError: any) {
      console.error(reviewError);
      setError(reviewError?.message || 'Failed to run guideline review.');
    } finally {
      setLoadingReview(false);
    }
  };

  const handleSaveReport = async () => {
    if (!currentReport) return;
    setSavingReport(true);
    setError(null);
    try {
      await AppViolationService.saveReport(currentReport);
      await loadReports();
      setSuccess('Guideline review report saved.');
    } catch (saveError: any) {
      console.error(saveError);
      setError(saveError?.message || 'Failed to save guideline review report.');
    } finally {
      setSavingReport(false);
    }
  };

  const handleDeleteReport = async (report: AppViolationReviewReport) => {
    if (!user?.email) return;
    if (!window.confirm('Delete this guideline review report?')) return;
    try {
      await AppViolationService.deleteReport(user.email, report.reportId);
      await loadReports();
      if (currentReport?.reportId === report.reportId) {
        setCurrentReport(null);
      }
      setSuccess('Guideline review report deleted.');
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || 'Failed to delete report.');
    }
  };

  const copyText = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(successMessage);
      setError(null);
    } catch (copyError) {
      console.error(copyError);
      setError('Failed to copy text.');
    }
  };

  const buildIssueFixStepsText = (issue: ViolationIssue) => {
    if (!issue.fixSteps.length) return `${issue.title}\nNo fix steps provided.`;
    return `${issue.title}\n${issue.fixSteps.map((step, index) => `${index + 1}. ${step}`).join('\n')}`;
  };

  const buildAllFixStepsText = (report: AppViolationReviewReport) => {
    const lines: string[] = [];
    report.groupedFindings.forEach((group) => {
      lines.push(group.platform === 'ios' ? 'Apple App Store' : 'Google Play');
      if (!group.issues.length) {
        lines.push('No issues found.');
        lines.push('');
        return;
      }
      group.issues.forEach((issue, issueIndex) => {
        lines.push(`${issueIndex + 1}. ${issue.title}`);
        if (issue.fixSteps.length) {
          issue.fixSteps.forEach((step, stepIndex) => {
            lines.push(`   ${stepIndex + 1}) ${step}`);
          });
        } else {
          lines.push('   No fix steps provided.');
        }
      });
      lines.push('');
    });
    return lines.join('\n').trim();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        App Store Violation Checker
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload app screenshots, detect Apple App Store and Google Play policy risks, and save compliance reports per registered app.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="New Review" />
          <Tab label="Current Report" />
          <Tab label="Saved Reports" />
        </Tabs>
      </Box>

      {tab === 0 ? (
        <Paper sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Select a registered app"
                value={selectedAppId}
                onChange={(event) => setSelectedAppId(event.target.value)}
              >
                <MenuItem value="">
                  <em>Select a registered app</em>
                </MenuItem>
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Review Model (Optional)"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              >
                {REVIEW_MODELS.map((option) => (
                  <MenuItem key={option.value || 'default'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                Upload Screenshots
                <input hidden multiple accept="image/*" type="file" onChange={handleUploadScreenshots} />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {screenshots.length} selected
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {screenshots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No screenshots uploaded yet.
                </Typography>
              ) : (
                <Grid container spacing={1.2}>
                  {screenshots.map((file, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`${file.name}-${file.size}-${index}`}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          position: 'relative',
                          cursor: 'pointer',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                        onClick={() => {
                          setPreviewSource('upload');
                          setPreviewIndex(index);
                          setPreviewOpen(true);
                        }}
                      >
                        <Button
                          size="small"
                          color="error"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveScreenshot(index);
                          }}
                          sx={{
                            position: 'absolute',
                            minWidth: 0,
                            width: 24,
                            height: 24,
                            p: 0,
                            top: 6,
                            right: 6,
                            zIndex: 2,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.9)',
                          }}
                        >
                          <Close sx={{ fontSize: 16 }} />
                        </Button>
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '9 / 16',
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: '#f4f6f8',
                            mb: 0.8,
                          }}
                        >
                          <Box
                            component="img"
                            src={screenshotPreviewUrls[index]}
                            alt={file.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block' }} noWrap title={file.name}>
                          {file.name}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleRunReview}
                disabled={loadingReview || !selectedApp || screenshots.length === 0}
              >
                {loadingReview ? 'Reviewing...' : 'Run Violation Review'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ) : null}

      {tab === 1 ? (
        !currentReport ? (
          <Alert severity="info">Run a guideline review or open a saved report.</Alert>
        ) : (
          <Box>
            <Paper sx={{ p: 2.5, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {currentReport.appName} Guideline Review
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Model: {currentReport.modelUsed} | Screenshots: {currentReport.reviewMetadata.screenshotCount} | Duration: {currentReport.reviewMetadata.durationSeconds}s
                  </Typography>
                  </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={() =>
                      copyText(
                        buildAllFixStepsText(currentReport),
                        'Copied all fix steps for both platforms.'
                      )
                    }
                  >
                    Copy All Fix Steps
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    disabled={savingReport}
                    onClick={handleSaveReport}
                  >
                    {savingReport ? 'Saving...' : 'Save Report'}
                  </Button>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ mt: 2 }}>
                {currentReport.summary}
              </Typography>
            </Paper>

            <Grid container spacing={2}>
              {currentReport.groupedFindings.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="success">
                    No actionable App Store / Play Store violations were detected in the uploaded screenshots.
                  </Alert>
                </Grid>
              ) : (
                currentReport.groupedFindings.map((group) => (
                  <Grid item xs={12} md={6} key={`platform-${group.platform}`}>
                    <Paper sx={{ p: 2.5, height: '100%' }}>
                      <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 1 }}>
                        {group.platform === 'ios' ? 'Apple App Store' : 'Google Play'} ({group.totalIssues})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {group.overview || 'No overview provided.'}
                      </Typography>
                      {group.issues.map((issue) => (
                        <ViolationIssueCard
                          key={issue.id}
                          issue={issue}
                          resolveScreenshotRef={(ref) =>
                            screenshotPreviewLookup.get(ref.trim().toLowerCase()) || null
                          }
                          onOpenScreenshot={(index) => {
                            setPreviewSource('report');
                            setPreviewIndex(index);
                            setPreviewOpen(true);
                          }}
                          onCopyFixSteps={() =>
                            copyText(
                              buildIssueFixStepsText(issue),
                              `Copied fix steps for "${issue.title}".`
                            )
                          }
                        />
                      ))}
                    </Paper>
                  </Grid>
                ))
              )}

              <Grid item xs={12}>
                <Paper sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      Global Fix Recommendations
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      disabled={currentReport.globalFixRecommendations.length === 0}
                      onClick={() =>
                        copyText(
                          currentReport.globalFixRecommendations.join('\n'),
                          'Copied all global fix recommendations.'
                        )
                      }
                    >
                      Copy All
                    </Button>
                  </Box>
                  {currentReport.globalFixRecommendations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No additional global recommendations.
                    </Typography>
                  ) : (
                    currentReport.globalFixRecommendations.map((item, index) => (
                      <Typography key={`global-fix-${index}`} variant="body2" sx={{ mb: 0.8 }}>
                        - {formatDisplayText(item)}
                      </Typography>
                    ))
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )
      ) : null}

      {tab === 2 ? (
        savedReports.length === 0 ? (
          <Alert severity="info">No saved guideline review reports for the selected app.</Alert>
        ) : (
          <Grid container spacing={2}>
            {savedReports.map((report) => (
              <Grid item xs={12} md={6} key={report.reportId}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{report.appName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {report.savedAt?.toLocaleString() || report.reviewMetadata.generatedAt.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {report.summary}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Findings: {report.groupedFindings.reduce((sum, group) => sum + group.totalIssues, 0)} | Model: {report.modelUsed}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => {
                        setCurrentReport(report);
                        setTab(1);
                      }}
                    >
                      Open
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDeleteReport(report)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      ) : null}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 2 }}>
          {activePreviewItems.length > 0 && activePreviewItems[previewIndex]?.url ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {activePreviewItems[previewIndex]?.fileName || `Screenshot ${previewIndex + 1}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {previewIndex + 1} / {activePreviewItems.length}
                </Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: '70vh',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: '#111827',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={activePreviewItems[previewIndex]?.url}
                  alt={activePreviewItems[previewIndex]?.fileName}
                  sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.2 }}>
                <Button
                  size="small"
                  startIcon={<ArrowBack />}
                  disabled={previewIndex <= 0}
                  onClick={() => setPreviewIndex((prev) => Math.max(0, prev - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  endIcon={<ArrowForward />}
                  disabled={previewIndex >= activePreviewItems.length - 1}
                  onClick={() =>
                    setPreviewIndex((prev) => Math.min(activePreviewItems.length - 1, prev + 1))
                  }
                >
                  Next
                </Button>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function ViolationIssueCard({
  issue,
  resolveScreenshotRef,
  onOpenScreenshot,
  onCopyFixSteps,
}: {
  issue: ViolationIssue;
  resolveScreenshotRef: (ref: string) => { url: string; index: number; fileName: string } | null;
  onOpenScreenshot: (index: number) => void;
  onCopyFixSteps: () => void;
}) {
  const severityColor =
    issue.severity === 'critical'
      ? 'error.main'
      : issue.severity === 'high'
      ? 'warning.main'
      : issue.severity === 'medium'
      ? 'info.main'
      : 'text.secondary';

  return (
    <Box sx={{ p: 1.4, border: '1px solid #eceff3', borderRadius: 1, mb: 1.2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {formatDisplayText(issue.title)}
      </Typography>
      <Typography variant="caption" sx={{ color: severityColor, textTransform: 'uppercase' }}>
        {issue.severity} severity
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.8 }}>
        <strong>Guideline:</strong> {formatDisplayText(issue.guidelineReference)}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.6 }}>
        <strong>Why this can be rejected:</strong> {formatDisplayText(issue.whyItCanBeRejected)}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.6 }}>
        <strong>Screenshots:</strong>
      </Typography>
      {issue.screenshotRefs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Not specified
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.6 }}>
          {issue.screenshotRefs.map((ref, index) => {
            const normalized = formatDisplayText(ref);
            const matched = resolveScreenshotRef(normalized);
            if (!matched) {
              return (
                <Chip
                  key={`${issue.id}-ss-${index}`}
                  size="small"
                  variant="outlined"
                  label={normalized}
                />
              );
            }
            return (
              <Box
                key={`${issue.id}-ss-${index}`}
                sx={{
                  width: 70,
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  p: 0.4,
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => onOpenScreenshot(matched.index)}
                title={`Open ${matched.fileName}`}
              >
                <Box
                  component="img"
                  src={matched.url}
                  alt={matched.fileName}
                  sx={{
                    width: '100%',
                    height: 90,
                    objectFit: 'cover',
                    borderRadius: 0.8,
                    display: 'block',
                    mb: 0.3,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ display: 'block', lineHeight: 1.1 }}
                  noWrap
                >
                  {matched.fileName}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
      <Typography variant="body2" sx={{ mt: 0.8, fontWeight: 600 }}>
        Fix Steps
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ContentCopy />}
        onClick={onCopyFixSteps}
        sx={{ mt: 0.6, mb: 0.8 }}
      >
        Copy Fix Steps
      </Button>
      {issue.fixSteps.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No fix steps provided.
        </Typography>
      ) : (
        issue.fixSteps.map((step, index) => (
          <Typography key={`${issue.id}-fix-${index}`} variant="body2">
            {index + 1}. {formatDisplayText(step)}
          </Typography>
        ))
      )}
    </Box>
  );
}

function formatDisplayText(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';

  return raw
    .replace(/^\s*[-•]\s*/, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/^\*+/, '')
    .replace(/\*+$/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
